import React from 'react'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { cn } from '@/lib/utils'

interface StatProps {
  icon?:        React.ReactNode
  label:        string
  value:        string | number
  trend?:       number   // percentage, positive = up, negative = down
  trendLabel?:  string
  className?:   string
  loading?:     boolean
  color?:       'blue' | 'green' | 'orange' | 'red' | 'purple'
}

const colorConfig = {
  blue:   { bg: 'bg-primary-50 dark:bg-primary-900/20', icon: 'text-primary-600 dark:text-primary-400' },
  green:  { bg: 'bg-emerald-50 dark:bg-emerald-900/20', icon: 'text-emerald-600 dark:text-emerald-400' },
  orange: { bg: 'bg-amber-50 dark:bg-amber-900/20',     icon: 'text-amber-600 dark:text-amber-400' },
  red:    { bg: 'bg-red-50 dark:bg-red-900/20',         icon: 'text-red-600 dark:text-red-400' },
  purple: { bg: 'bg-violet-50 dark:bg-violet-900/20',   icon: 'text-violet-600 dark:text-violet-400' },
}

export const Stat: React.FC<StatProps> = ({
  icon,
  label,
  value,
  trend,
  trendLabel,
  className,
  loading = false,
  color   = 'blue',
}) => {
  const cfg = colorConfig[color]

  const TrendIcon =
    trend === undefined || trend === 0 ? Minus : trend > 0 ? TrendingUp : TrendingDown

  const trendColor =
    trend === undefined || trend === 0
      ? 'text-slate-400'
      : trend > 0
        ? 'text-emerald-600 dark:text-emerald-400'
        : 'text-sport-red'

  return (
    <div
      className={cn(
        'card-base p-5 flex items-start gap-4',
        className,
      )}
    >
      {icon && (
        <div className={cn('rounded-xl p-2.5 flex-shrink-0', cfg.bg)}>
          <span className={cn('flex items-center justify-center [&>svg]:h-5 [&>svg]:w-5', cfg.icon)}>
            {icon}
          </span>
        </div>
      )}

      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-500 dark:text-slate-400 font-medium truncate">{label}</p>

        {loading ? (
          <div className="mt-1 skeleton h-8 w-28" />
        ) : (
          <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100 tabular-nums">
            {value}
          </p>
        )}

        {trend !== undefined && !loading && (
          <p className={cn('mt-1.5 flex items-center gap-1 text-xs font-medium', trendColor)}>
            <TrendIcon className="h-3.5 w-3.5" aria-hidden="true" />
            {trend > 0 ? '+' : ''}
            {trend}%{trendLabel && <span className="text-slate-400 font-normal ml-0.5">{trendLabel}</span>}
          </p>
        )}
      </div>
    </div>
  )
}
